/**
 * @fileoverview Calendar helpers (ported from lramos33/big-calendar)
 *
 * Pure date-fns + position-math helpers used by every view. Unmodified
 * from upstream except:
 *  - `getMonthCellEvents` `eventPositions` is keyed by event id which
 *    can now be a string (Convex id) — coerced to string for the lookup.
 *  - Identical to upstream otherwise so future merges stay easy.
 */

import {
  addDays,
  addMonths,
  addWeeks,
  subDays,
  subMonths,
  subWeeks,
  isSameWeek,
  isSameDay,
  isSameMonth,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  differenceInMinutes,
  eachDayOfInterval,
  startOfDay,
  differenceInDays,
  endOfYear,
  startOfYear,
  subYears,
  addYears,
  isSameYear,
  isWithinInterval,
} from "date-fns";

import type { ICalendarCell, IEvent } from "@/calendar/interfaces";
import type {
  TCalendarView,
  TVisibleHours,
  TWorkingHours,
} from "@/calendar/types";

// ================ Header helper functions ================ //

export function rangeText(view: TCalendarView, date: Date) {
  const formatString = "MMM d, yyyy";

  switch (view) {
    case "list":
      // The list view ignores the navigator window; surface a static
      // "All key dates" label so users don't try to scrub through.
      return "All key dates";
    case "settings":
      // Not a calendar view — the settings panel has no date window.
      return "Calendar feed settings";
    case "day":
      return format(date, formatString);
  }

  const { start, end } = ((): { start: Date; end: Date } => {
    switch (view) {
      case "agenda":
        return { start: startOfMonth(date), end: endOfMonth(date) };
      case "year":
        return { start: startOfYear(date), end: endOfYear(date) };
      case "month":
        return { start: startOfMonth(date), end: endOfMonth(date) };
      case "week":
        return { start: startOfWeek(date), end: endOfWeek(date) };
    }
  })();

  return `${format(start, formatString)} - ${format(end, formatString)}`;
}

export function navigateDate(
  date: Date,
  view: TCalendarView,
  direction: "previous" | "next",
): Date {
  // List and settings views have no calendar window, so prev/next is a no-op.
  if (view === "list" || view === "settings") return date;

  const operations = {
    agenda: direction === "next" ? addMonths : subMonths,
    year: direction === "next" ? addYears : subYears,
    month: direction === "next" ? addMonths : subMonths,
    week: direction === "next" ? addWeeks : subWeeks,
    day: direction === "next" ? addDays : subDays,
  } satisfies Record<
    Exclude<TCalendarView, "list" | "settings">,
    typeof addDays
  >;

  return operations[view](date, 1);
}

export function getEventsCount(
  events: IEvent[],
  date: Date,
  view: TCalendarView,
): number {
  // List view shows everything regardless of `date`, so the badge
  // counts the entire event list.
  if (view === "list") return events.length;
  // Settings is not a calendar view — no count badge.
  if (view === "settings") return 0;

  const compareFns = {
    agenda: isSameMonth,
    year: isSameYear,
    day: isSameDay,
    week: isSameWeek,
    month: isSameMonth,
  } satisfies Record<
    Exclude<TCalendarView, "list" | "settings">,
    typeof isSameDay
  >;

  return events.filter((event) =>
    compareFns[view](new Date(event.startDate), date),
  ).length;
}

// ================ Week and day view helper functions ================ //

export function getCurrentEvents(events: IEvent[]) {
  const now = new Date();
  return (
    events.filter((event) =>
      isWithinInterval(now, {
        start: parseISO(event.startDate),
        end: parseISO(event.endDate),
      }),
    ) || null
  );
}

export function groupEvents(dayEvents: IEvent[]) {
  const sortedEvents = dayEvents.sort(
    (a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime(),
  );
  const groups: IEvent[][] = [];

  for (const event of sortedEvents) {
    const eventStart = parseISO(event.startDate);

    const targetGroup = groups.find((group) => {
      const lastEventInGroup = group[group.length - 1];
      const lastEventEnd = parseISO(lastEventInGroup.endDate);
      return eventStart >= lastEventEnd;
    });

    if (targetGroup) {
      targetGroup.push(event);
    } else {
      groups.push([event]);
    }
  }

  return groups;
}

function getTopPercent(
  startMinutes: number,
  visibleHoursRange?: { from: number; to: number },
) {
  if (!visibleHoursRange) return (startMinutes / 1440) * 100;

  const visibleStartMinutes = visibleHoursRange.from * 60;
  const visibleEndMinutes = visibleHoursRange.to * 60;
  const visibleRangeMinutes = visibleEndMinutes - visibleStartMinutes;
  return ((startMinutes - visibleStartMinutes) / visibleRangeMinutes) * 100;
}

export function getEventBlockStyle(
  event: IEvent,
  day: Date,
  groupIndex: number,
  groupSize: number,
  visibleHoursRange?: { from: number; to: number },
) {
  const startDate = parseISO(event.startDate);
  const dayStart = new Date(day.setHours(0, 0, 0, 0));
  const eventStart = startDate < dayStart ? dayStart : startDate;
  const startMinutes = differenceInMinutes(eventStart, dayStart);

  const top = getTopPercent(startMinutes, visibleHoursRange);

  const width = 100 / groupSize;
  const left = groupIndex * width;

  return { top: `${top}%`, width: `${width}%`, left: `${left}%` };
}

export function isWorkingHour(
  day: Date,
  hour: number,
  workingHours: TWorkingHours,
) {
  const dayIndex = day.getDay() as keyof typeof workingHours;
  const dayHours = workingHours[dayIndex];
  return hour >= dayHours.from && hour < dayHours.to;
}

export function getVisibleHours(
  visibleHours: TVisibleHours,
  singleDayEvents: IEvent[],
) {
  const bounds = singleDayEvents.reduce(
    (acc, event) => {
      const startHour = parseISO(event.startDate).getHours();
      const endTime = parseISO(event.endDate);
      const endHour = endTime.getHours() + (endTime.getMinutes() > 0 ? 1 : 0);
      return {
        earliestEventHour: Math.min(acc.earliestEventHour, startHour),
        latestEventHour: Math.max(acc.latestEventHour, endHour),
      };
    },
    { earliestEventHour: visibleHours.from, latestEventHour: visibleHours.to },
  );

  const earliestEventHour = bounds.earliestEventHour;
  const latestEventHour = Math.min(bounds.latestEventHour, 24);

  const hours = Array.from(
    { length: latestEventHour - earliestEventHour },
    (_, i) => i + earliestEventHour,
  );

  return { hours, earliestEventHour, latestEventHour };
}

// ================ Month view helper functions ================ //

export function getCalendarCells(selectedDate: Date): ICalendarCell[] {
  const currentYear = selectedDate.getFullYear();
  const currentMonth = selectedDate.getMonth();

  const getDaysInMonth = (year: number, month: number) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) =>
    new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
  const daysInPrevMonth = getDaysInMonth(currentYear, currentMonth - 1);
  const totalDays = firstDayOfMonth + daysInMonth;

  const prevMonthCells = Array.from({ length: firstDayOfMonth }, (_, i) => ({
    day: daysInPrevMonth - firstDayOfMonth + i + 1,
    currentMonth: false,
    date: new Date(
      currentYear,
      currentMonth - 1,
      daysInPrevMonth - firstDayOfMonth + i + 1,
    ),
  }));

  const currentMonthCells = Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    currentMonth: true,
    date: new Date(currentYear, currentMonth, i + 1),
  }));

  const nextMonthCells = Array.from(
    { length: (7 - (totalDays % 7)) % 7 },
    (_, i) => ({
      day: i + 1,
      currentMonth: false,
      date: new Date(currentYear, currentMonth + 1, i + 1),
    }),
  );

  return [...prevMonthCells, ...currentMonthCells, ...nextMonthCells];
}

export function calculateMonthEventPositions(
  multiDayEvents: IEvent[],
  singleDayEvents: IEvent[],
  selectedDate: Date,
) {
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);

  const eventPositions: { [key: string]: number } = {};
  const occupiedPositions: { [key: string]: boolean[] } = {};

  eachDayOfInterval({ start: monthStart, end: monthEnd }).forEach((day) => {
    occupiedPositions[day.toISOString()] = [false, false, false];
  });

  const sortedEvents = [
    ...multiDayEvents.sort((a, b) => {
      const aDuration = differenceInDays(
        parseISO(a.endDate),
        parseISO(a.startDate),
      );
      const bDuration = differenceInDays(
        parseISO(b.endDate),
        parseISO(b.startDate),
      );
      return (
        bDuration - aDuration ||
        parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()
      );
    }),
    ...singleDayEvents.sort(
      (a, b) =>
        parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime(),
    ),
  ];

  sortedEvents.forEach((event) => {
    const eventStart = parseISO(event.startDate);
    const eventEnd = parseISO(event.endDate);
    const eventDays = eachDayOfInterval({
      start: eventStart < monthStart ? monthStart : eventStart,
      end: eventEnd > monthEnd ? monthEnd : eventEnd,
    });

    const position =
      [0, 1, 2].find((i) =>
        eventDays.every((day) => {
          const dayPositions = occupiedPositions[startOfDay(day).toISOString()];
          return dayPositions && !dayPositions[i];
        }),
      ) ?? -1;

    if (position !== -1) {
      eventDays.forEach((day) => {
        const dayKey = startOfDay(day).toISOString();
        occupiedPositions[dayKey][position] = true;
      });
      eventPositions[String(event.id)] = position;
    }
  });

  return eventPositions;
}

export function getMonthCellEvents(
  date: Date,
  events: IEvent[],
  eventPositions: Record<string, number>,
) {
  const eventsForDate = events.filter((event) => {
    const eventStart = parseISO(event.startDate);
    const eventEnd = parseISO(event.endDate);
    return (
      (date >= eventStart && date <= eventEnd) ||
      isSameDay(date, eventStart) ||
      isSameDay(date, eventEnd)
    );
  });

  return eventsForDate
    .map((event) => ({
      ...event,
      position: eventPositions[String(event.id)] ?? -1,
      isMultiDay: event.startDate !== event.endDate,
    }))
    .sort((a, b) => {
      if (a.isMultiDay && !b.isMultiDay) return -1;
      if (!a.isMultiDay && b.isMultiDay) return 1;
      return a.position - b.position;
    });
}
